import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FormSelect } from '@podus/ui';
import ChartCardHeader from './ChartCardHeader';

export interface LineSeriesConfig {
    dataKey: string;
    stroke: string;
    name: string;
}

export interface LegendBoxConfig {
    label: string;
    value: string;
    dotColor: string;
    textColor: string;
    bgColor: string;
    borderColor: string;
}

export interface LineChartCardProps {
    title: string;
    data: Record<string, unknown>[];
    series: LineSeriesConfig[];
    xDataKey?: string;
    yTickFormatter?: (value: number) => string;
    tooltipFormatter?: (value: unknown) => string;
    legendBoxes?: LegendBoxConfig[];
    height?: number;
    year?: number;
    onYearChange?: (year: number) => void;
    yearsBack?: number;
}

const currentYear = new Date().getFullYear();

export default function LineChartCard({
    title,
    data,
    series,
    xDataKey = 'month',
    yTickFormatter,
    tooltipFormatter,
    legendBoxes,
    height = 220,
    year,
    onYearChange,
    yearsBack = 5,
}: LineChartCardProps) {
    const years = Array.from({ length: yearsBack + 1 }, (_, i) => currentYear - i);

    return (
        <div className="rounded-xl border border-[#E0E0E0] p-5">
            <ChartCardHeader />
            <h3 className="mt-1 text-center font-bold text-secondary text-2xl">{title}</h3>

            {/* Legend boxes + time range */}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-[14px]">
                {legendBoxes?.map((box) => (
                    <div
                        key={box.label}
                        className="rounded-lg border px-4 py-2"
                        style={{ borderColor: box.borderColor, backgroundColor: box.bgColor }}
                    >
                        <div className="flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: box.dotColor }} />
                            <span className="text-[13px]" style={{ color: box.textColor }}>
                                {box.label}
                            </span>
                        </div>
                        <p className="mt-0.5 text-lg font-bold" style={{ color: box.textColor }}>
                            {box.value}
                        </p>
                    </div>
                ))}
                {onYearChange && (
                    <div className="ml-auto w-36">
                        <FormSelect
                            title="Year"
                            value={String(year ?? currentYear)}
                            onChange={(v) => onYearChange(Number(v))}
                            options={years.map((y) => ({ label: String(y), value: String(y) }))}
                        />
                    </div>
                )}
            </div>

            <div className="mt-4">
                <ResponsiveContainer width="100%" height={height}>
                    <LineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                            dataKey={xDataKey}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#999', fontSize: 12 }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#999', fontSize: 12 }}
                            tickFormatter={yTickFormatter}
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 8,
                                border: '1px solid #eee',
                                fontSize: 13,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}
                            formatter={tooltipFormatter ? (value) => tooltipFormatter(value) : undefined}
                        />
                        {series.map((s) => (
                            <Line
                                key={s.dataKey}
                                type="monotone"
                                dataKey={s.dataKey}
                                stroke={s.stroke}
                                strokeWidth={2}
                                dot={false}
                                name={s.name}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
